// src/pages/eglises/AjouterEglise.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  /* Card */
  .card-modern {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s;
  }
  .card-modern:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .card-modern.border-primary {
    border-top-color: ${COLORS.primary};
  }

  .card-header-modern {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.8rem 1.2rem;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .card-header-modern i {
    color: ${COLORS.secondary};
  }

  .card-body-modern {
    padding: 1.5rem;
  }

  /* Stepper */
  .stepper-wrapper {
    padding: 0.5rem 0;
  }

  .stepper-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
    flex: 1;
  }

  .step-circle {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background: #e9edf2;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    margin-bottom: 6px;
    transition: all 0.3s ease;
  }

  .step.completed .step-circle {
    background: ${COLORS.primary};
    color: white;
  }

  .step.active .step-circle {
    background: ${COLORS.primary};
    color: white;
    box-shadow: 0 0 0 3px ${COLORS.primary}40, 0 4px 10px rgba(41,90,167,0.2);
    transform: scale(1.05);
  }

  .step-label {
    font-size: 0.65rem;
    font-weight: 500;
    color: #94a3b8;
    text-align: center;
    transition: all 0.3s ease;
  }

  .step.active .step-label {
    color: ${COLORS.primary};
    font-weight: 600;
  }

  .step.completed .step-label {
    color: ${COLORS.primary};
  }

  .step-connector {
    flex: 1;
    height: 2px;
    background: #e9edf2;
    margin: 0 8px;
    position: relative;
    top: -10px;
    border-radius: 2px;
    transition: all 0.3s ease;
  }

  .step-connector.completed {
    background: ${COLORS.primary};
  }

  .progress-modern {
    height: 3px;
    border-radius: 2px;
    background: #e9edf2;
    overflow: hidden;
    margin-top: 0.5rem;
  }

  .progress-modern .progress-bar {
    background: ${COLORS.primary};
    transition: width 0.5s ease;
  }

  /* Form */
  .form-label-modern {
    font-size: 0.7rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.2rem;
  }
  .form-label-modern .required {
    color: ${COLORS.danger};
    margin-left: 2px;
  }

  .form-control-modern, .form-select-modern {
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.4rem 0.7rem;
    font-size: 0.85rem;
    transition: all 0.2s ease;
    background: white;
  }
  .form-control-modern:focus, .form-select-modern:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 3px rgba(41,90,167,0.08);
  }

  /* Quill */
  .quill-wrapper {
    background: white;
    border-radius: 4px;
    overflow: hidden;
  }
  .quill-wrapper .ql-container {
    min-height: 120px;
    font-size: 0.85rem;
    border: 1px solid #e2e8f0;
    border-top: none;
  }
  .quill-wrapper .ql-toolbar {
    border: 1px solid #e2e8f0;
    border-radius: 4px 4px 0 0;
    background: #f8fafc;
  }
  .quill-wrapper .ql-editor {
    min-height: 120px;
  }

  /* Upload */
  .upload-area {
    border: 2px dashed #e2e8f0;
    border-radius: 4px;
    padding: 2rem;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fafbfc;
  }
  .upload-area:hover {
    border-color: ${COLORS.primary};
    background: rgba(41,90,167,0.02);
  }
  .upload-icon {
    font-size: 2.8rem;
    color: ${COLORS.primary};
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }
  .upload-text {
    color: #1e293b;
    font-size: 0.9rem;
    font-weight: 500;
  }
  .upload-hint {
    color: #94a3b8;
    font-size: 0.75rem;
  }
  .upload-preview {
    max-width: 100%;
    max-height: 180px;
    border-radius: 4px;
    object-fit: cover;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  /* Notifications */
  .toast-notification {
    padding: 0.8rem 1rem;
    border: none;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 1.5rem;
    animation: slideDown 0.3s ease-out;
    border-radius: 4px;
  }
  .toast-notification-success {
    background: #f0fdf4;
    border-left: 3px solid ${COLORS.success};
  }
  .toast-notification-error {
    background: #fef2f2;
    border-left: 3px solid ${COLORS.danger};
  }
  .toast-icon {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1rem;
  }
  .toast-icon-success {
    background: rgba(16,185,129,0.15);
    color: ${COLORS.success};
  }
  .toast-icon-error {
    background: rgba(239,68,68,0.15);
    color: ${COLORS.danger};
  }
  .toast-content {
    flex: 1;
  }
  .toast-title {
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 1px;
  }
  .toast-message {
    font-size: 0.75rem;
    color: #64748b;
    margin: 0;
  }
  .toast-close {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 2px;
    font-size: 1rem;
  }

  .step-content {
    min-height: 300px;
  }

  /* Animations */
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
    .step-label {
      font-size: 0.55rem;
    }
    .step-circle {
      width: 30px;
      height: 30px;
      font-size: 0.7rem;
    }
    .card-body-modern {
      padding: 1rem;
    }
  }
`;

export default function AjouterEglise() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    nom: '',
    tel: '',
    email: '',
    province: '',
    ville: '',
    adresse: '',
    date_at: '',
    caisse: 1,
    logo: null,
    titre: '',
    description: '',
    vision: '',
    mission: '',
    action: '',
    culte: '',
    reference: 'Allez, faites de toutes les nations des disciples',
    chapitre: 'Matthieu 28:19-20 LSG',
    matricule_admin: '',
    nom_admin: '',
    prenom_admin: '',
    datenaiss_admin: '',
    lieu_admin: '',
    contact_admin: '',
    email_admin: '',
    gendre_admin: 'Homme',
    date_deb_abon: '',
    date_fin_abon: '',
    montant_abon: ''
  });

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link'
  ];

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file' && files[0]) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
      setPreviewImage(URL.createObjectURL(files[0]));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? parseInt(value) || 0 : value
      }));
    }
  };

  const handleQuillChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'logo' && formData.logo) {
          submitData.append('files', formData.logo);
        } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      const response = await fetch(`https://josephsbusiness.com/api/eglises_complete.php?user_id=${userId}`, {
        method: 'POST',
        body: submitData
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(`Église ajoutée avec succès - Matricule: ${result.matricule}`);
        setTimeout(() => {
          navigate('/eglises/sieges');
        }, 2000);
      } else {
        setError(result.message || 'Erreur lors de l\'ajout');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = (e) => {
    e.preventDefault();
    if (currentStep === 1) {
      if (!formData.nom || !formData.ville || !formData.date_at) {
        setError('Veuillez remplir tous les champs obligatoires (Nom, Ville, Date de création)');
        return;
      }
    } else if (currentStep === 4) {
      if (!formData.nom_admin || !formData.prenom_admin || !formData.email_admin) {
        setError('Veuillez remplir tous les champs obligatoires de l\'administrateur');
        return;
      }
    } else if (currentStep === 5) {
      if (!formData.date_deb_abon || !formData.date_fin_abon || !formData.montant_abon) {
        setError('Veuillez remplir tous les champs de l\'abonnement');
        return;
      }
    }
    setError('');
    setCurrentStep(prev => Math.min(prev + 1, 5));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = (e) => {
    e.preventDefault();
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  return (
    <>
      <style>{styles}</style>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-add-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Ajouter une église</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-building-line"></i> Nouvelle église
                  </span>
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button size="sm" className="btn-modern-outline" onClick={() => navigate('/eglises/sieges')}>
                <i className="ri-arrow-left-line"></i> Retour
              </Button>
            </div>
          </div>

          {/* ===== NOTIFICATIONS ===== */}
          {error && (
            <div className="toast-notification toast-notification-error mb-4">
              <div className="toast-icon toast-icon-error">
                <i className="ri-error-warning-line"></i>
              </div>
              <div className="toast-content">
                <div className="toast-title" style={{ color: COLORS.danger }}>Erreur</div>
                <p className="toast-message">{error}</p>
              </div>
              <button className="toast-close" onClick={() => setError('')}>
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {success && (
            <div className="toast-notification toast-notification-success mb-4">
              <div className="toast-icon toast-icon-success">
                <i className="ri-checkbox-circle-line"></i>
              </div>
              <div className="toast-content">
                <div className="toast-title" style={{ color: COLORS.success }}>Succès</div>
                <p className="toast-message">{success}</p>
              </div>
              <button className="toast-close" onClick={() => setSuccess('')}>
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* ===== CARD PRINCIPALE ===== */}
          <Row className="justify-content-center">
            <Col md={11}>
              <Card className="card-modern border-primary">
                <Card.Header className="card-header-modern">
                  <i className="ri-information-line"></i> Formulaire d'ajout
                </Card.Header>
                <Card.Body className="card-body-modern">

                  {/* Stepper */}
                  <div className="stepper-wrapper mb-4">
                    <div className="stepper-container">
                      {[1, 2, 3, 4, 5].map((step) => (
                        <React.Fragment key={step}>
                          <div className={`step ${currentStep >= step ? 'completed' : ''} ${currentStep === step ? 'active' : ''}`}>
                            <div className="step-circle">{step}</div>
                            <div className="step-label">
                              {step === 1 && 'Infos église'}
                              {step === 2 && 'Logo'}
                              {step === 3 && 'Page accueil'}
                              {step === 4 && 'Admin'}
                              {step === 5 && 'Abonnement'}
                            </div>
                          </div>
                          {step < 5 && (
                            <div className={`step-connector ${currentStep > step ? 'completed' : ''}`}></div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="progress-modern">
                      <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                    </div>
                  </div>

                  {/* Formulaire */}
                  <Form onSubmit={handleSubmit}>

                    {/* ÉTAPE 1 - Informations de l'église */}
                    {currentStep === 1 && (
                      <div className="step-content fade-in">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-information-line me-2"></i> Informations générales
                        </h6>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="nom">
                              <Form.Label className="form-label-modern">
                                Nom <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="nom"
                                value={formData.nom}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="tel">
                              <Form.Label className="form-label-modern">Téléphone</Form.Label>
                              <Form.Control
                                type="text"
                                name="tel"
                                value={formData.tel}
                                onChange={handleChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="email">
                              <Form.Label className="form-label-modern">Email</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="province">
                              <Form.Label className="form-label-modern">Province</Form.Label>
                              <Form.Select
                                name="province"
                                value={formData.province}
                                onChange={handleChange}
                                className="form-select-modern"
                              >
                                <option value="">Sélectionner une province</option>
                                <option value="Estuaire">Estuaire</option>
                                <option value="Haut-Ogooué">Haut-Ogooué</option>
                                <option value="Moyen-Ogooué">Moyen-Ogooué</option>
                                <option value="Ngounié">Ngounié</option>
                                <option value="Nyanga">Nyanga</option>
                                <option value="Ogooué-Ivindo">Ogooué-Ivindo</option>
                                <option value="Ogooué-Lolo">Ogooué-Lolo</option>
                                <option value="Ogooué-Maritime">Ogooué-Maritime</option>
                                <option value="Woleu-Ntem">Woleu-Ntem</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="ville">
                              <Form.Label className="form-label-modern">
                                Ville <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="ville"
                                value={formData.ville}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="adresse">
                              <Form.Label className="form-label-modern">Adresse</Form.Label>
                              <Form.Control
                                type="text"
                                name="adresse"
                                value={formData.adresse}
                                onChange={handleChange}
                                className="form-control-modern"
                                placeholder="Quartier, rue, numéro..."
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="date_at">
                              <Form.Label className="form-label-modern">
                                Date de création <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="date_at"
                                value={formData.date_at}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="caisse">
                              <Form.Label className="form-label-modern">Caisse nationale ?</Form.Label>
                              <Form.Select
                                name="caisse"
                                value={formData.caisse}
                                onChange={handleChange}
                                className="form-select-modern"
                              >
                                <option value="1">Oui</option>
                                <option value="0">Non</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* ÉTAPE 2 - Logo */}
                    {currentStep === 2 && (
                      <div className="step-content fade-in text-center">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-image-line me-2"></i> Logo de l'église
                        </h6>
                        <p className="text-muted small mb-4">Téléchargez le logo qui apparaîtra sur la page d'accueil.</p>
                        <Row className="justify-content-center">
                          <Col md={8}>
                            <div className="upload-container">
                              <div
                                className="upload-area"
                                onClick={() => document.getElementById('logo-upload').click()}
                              >
                                {previewImage ? (
                                  <img src={previewImage} alt="Prévisualisation" className="upload-preview" />
                                ) : (
                                  <>
                                    <i className="ri-image-add-line upload-icon"></i>
                                    <p className="upload-text">Cliquez pour télécharger un logo</p>
                                    <span className="upload-hint">JPG, PNG, WEBP (max. 2 Mo)</span>
                                  </>
                                )}
                              </div>
                              <input
                                type="file"
                                id="logo-upload"
                                name="logo"
                                accept=".jpg,.png,image/jpeg,image/png,image/webp"
                                onChange={handleChange}
                                style={{ display: 'none' }}
                              />
                              {previewImage && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="mt-2 text-muted"
                                  style={{ textDecoration: 'none' }}
                                  onClick={() => {
                                    URL.revokeObjectURL(previewImage);
                                    setPreviewImage(null);
                                    setFormData(prev => ({ ...prev, logo: null }));
                                  }}
                                >
                                  <i className="ri-close-line me-1"></i> Supprimer
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* ÉTAPE 3 - Page de bienvenue */}
                    {currentStep === 3 && (
                      <div className="step-content fade-in">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-file-text-line me-2"></i> Page de bienvenue
                        </h6>
                        <Row className="g-3">
                          <Col md={12}>
                            <Form.Group controlId="titre">
                              <Form.Label className="form-label-modern">Titre de bienvenue</Form.Label>
                              <Form.Control
                                type="text"
                                name="titre"
                                value={formData.titre}
                                onChange={handleChange}
                                className="form-control-modern"
                                placeholder="Titre de la page d'accueil"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="description">
                              <Form.Label className="form-label-modern">Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={3}
                                className="form-control-modern"
                                placeholder="Description de l'église"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="vision">
                              <Form.Label className="form-label-modern">Vision</Form.Label>
                              <Form.Control
                                as="textarea"
                                name="vision"
                                value={formData.vision}
                                onChange={handleChange}
                                rows={3}
                                className="form-control-modern"
                                placeholder="Vision de l'église"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group controlId="mission">
                              <Form.Label className="form-label-modern">Mission</Form.Label>
                              <div className="quill-wrapper">
                                <ReactQuill
                                  theme="snow"
                                  value={formData.mission}
                                  onChange={(content) => handleQuillChange('mission', content)}
                                  modules={quillModules}
                                  formats={quillFormats}
                                  placeholder="Décrivez la mission de l'église..."
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="action">
                              <Form.Label className="form-label-modern">Actions</Form.Label>
                              <div className="quill-wrapper">
                                <ReactQuill
                                  theme="snow"
                                  value={formData.action}
                                  onChange={(content) => handleQuillChange('action', content)}
                                  modules={quillModules}
                                  formats={quillFormats}
                                  placeholder="Actions principales de l'église..."
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="culte">
                              <Form.Label className="form-label-modern">Informations sur le culte</Form.Label>
                              <div className="quill-wrapper">
                                <ReactQuill
                                  theme="snow"
                                  value={formData.culte}
                                  onChange={(content) => handleQuillChange('culte', content)}
                                  modules={quillModules}
                                  formats={quillFormats}
                                  placeholder="Horaires et informations des cultes..."
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="reference">
                              <Form.Label className="form-label-modern">Référence biblique</Form.Label>
                              <Form.Control
                                type="text"
                                name="reference"
                                value={formData.reference}
                                onChange={handleChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="chapitre">
                              <Form.Label className="form-label-modern">Chapitre biblique</Form.Label>
                              <Form.Control
                                type="text"
                                name="chapitre"
                                value={formData.chapitre}
                                onChange={handleChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* ÉTAPE 4 - Administrateur */}
                    {currentStep === 4 && (
                      <div className="step-content fade-in">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-user-settings-line me-2"></i> Informations de l'administrateur
                        </h6>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="matricule_admin">
                              <Form.Label className="form-label-modern">Matricule</Form.Label>
                              <Form.Control
                                type="text"
                                name="matricule_admin"
                                value={formData.matricule_admin}
                                onChange={handleChange}
                                className="form-control-modern"
                                placeholder="Généré automatiquement"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="nom_admin">
                              <Form.Label className="form-label-modern">
                                Nom <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="nom_admin"
                                value={formData.nom_admin}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="prenom_admin">
                              <Form.Label className="form-label-modern">
                                Prénom <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="prenom_admin"
                                value={formData.prenom_admin}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="datenaiss_admin">
                              <Form.Label className="form-label-modern">Date de naissance</Form.Label>
                              <Form.Control
                                type="date"
                                name="datenaiss_admin"
                                value={formData.datenaiss_admin}
                                onChange={handleChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="lieu_admin">
                              <Form.Label className="form-label-modern">Lieu de naissance</Form.Label>
                              <Form.Control
                                type="text"
                                name="lieu_admin"
                                value={formData.lieu_admin}
                                onChange={handleChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="contact_admin">
                              <Form.Label className="form-label-modern">Contact</Form.Label>
                              <Form.Control
                                type="text"
                                name="contact_admin"
                                value={formData.contact_admin}
                                onChange={handleChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="email_admin">
                              <Form.Label className="form-label-modern">
                                Email <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="email"
                                name="email_admin"
                                value={formData.email_admin}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="gendre_admin">
                              <Form.Label className="form-label-modern">Genre</Form.Label>
                              <Form.Select
                                name="gendre_admin"
                                value={formData.gendre_admin}
                                onChange={handleChange}
                                className="form-select-modern"
                              >
                                <option value="Homme">Homme</option>
                                <option value="Femme">Femme</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* ÉTAPE 5 - Abonnement */}
                    {currentStep === 5 && (
                      <div className="step-content fade-in">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-calendar-check-line me-2"></i> Abonnement
                        </h6>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="date_deb_abon">
                              <Form.Label className="form-label-modern">
                                Date de début <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="date_deb_abon"
                                value={formData.date_deb_abon}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="date_fin_abon">
                              <Form.Label className="form-label-modern">
                                Date de fin <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="date_fin_abon"
                                value={formData.date_fin_abon}
                                onChange={handleChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group controlId="montant_abon">
                              <Form.Label className="form-label-modern">
                                Montant payé <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="number"
                                name="montant_abon"
                                value={formData.montant_abon}
                                onChange={handleChange}
                                required
                                min="0"
                                className="form-control-modern"
                                placeholder="0 FCFA"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Boutons de navigation */}
                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                      <div>
                        {currentStep > 1 && (
                          <Button
                            variant="light"
                            size="sm"
                            className="btn-modern-outline"
                            onClick={prevStep}
                          >
                            <i className="ri-arrow-left-line me-1"></i> Précédent
                          </Button>
                        )}
                      </div>
                      <div>
                        {currentStep < totalSteps ? (
                          <Button
                            variant="primary"
                            size="sm"
                            className="btn-modern-primary"
                            onClick={nextStep}
                          >
                            Suivant <i className="ri-arrow-right-line ms-1"></i>
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            size="sm"
                            className="btn-modern-success"
                            disabled={loading}
                          >
                            {loading ? (
                              <><Spinner size="sm" className="me-1" /> Enregistrement...</>
                            ) : (
                              <><i className="ri-check-line me-1"></i> Ajouter l'église</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>
    </>
  );
}